import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Search } from 'lucide-react';

import { SecurityOverlay, DevToolsWarning, SecurityBadge } from '../components/SecureMessage';
import { SecurityScannerDialog, SecurityIndicator } from '../components/SecurityScanner';
import { VirtualKeyboard } from '../components/VirtualKeyboard';
import { useDecoyMode } from '../components/DecoyMode';
import { VoiceChannelRoom } from '../components/VoiceChannels';
import {
  useWindowFocus,
  useDevToolsDetection,
  usePreventScreenshotShortcuts,
  getSecurityStatus,
} from '../utils/security';
import { checkPendingNudges } from '../utils/nudgeFeedback';

import { Sidebar } from '../components/dashboard/Sidebar';
import { ConversationList } from '../components/dashboard/ConversationList';
import { ContactList } from '../components/dashboard/ContactList';
import { ChatView } from '../components/dashboard/ChatView';
import { EmptyChatView } from '../components/dashboard/EmptyChatView';
import { VoiceCallModal } from '../components/dashboard/VoiceCallModal';
import { GroupCallPanel } from '../components/dashboard/GroupCallPanel';
import { NewChatDialog } from '../components/dashboard/NewChatDialog';
import { SettingsDialog } from '../components/dashboard/SettingsDialog';
import { CommandPalette } from '../components/CommandPalette';
import { CommandPaletteOnboardingHint } from '../components/CommandPaletteOnboardingHint';
import { OnboardingTour } from '../components/OnboardingTour';
import { KeyRestorePrompt } from '../components/KeyRestorePrompt';
import { SessionActivityBanner } from '../components/SessionActivityBanner';
import { DuressAlertBanner } from '../components/DuressAlertBanner';
import { FloatingPanicButton } from '../components/FloatingPanicButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Dashboard = () => {
  const { user, addWsListener, isDuressMode, duressState } = useAuth();
  const [activeTab, setActiveTab] = useState('chats');
  const [conversations, setConversations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [showNewChat, setShowNewChat] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  // Optional anchor id passed into SettingsDialog so deep-links (e.g. from the
  // command palette's "Local data inspector" tile) scroll the right section
  // into view after the dialog opens. Reset to null whenever Settings closes.
  const [settingsScrollTo, setSettingsScrollTo] = useState(null);
  const [showSecurityScanner, setShowSecurityScanner] = useState(false);
  const [showVirtualKeyboard, setShowVirtualKeyboard] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [callData, setCallData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Voice channel state
  const [activeVoiceChannel, setActiveVoiceChannel] = useState(null);

  // Security hooks
  const isWindowFocused = useWindowFocus();
  const isDevToolsOpen = useDevToolsDetection();
  usePreventScreenshotShortcuts();

  // Decoy mode
  const { activateDecoy, isDecoyActive } = useDecoyMode();

  const securityStatus = getSecurityStatus(isWindowFocused, isDevToolsOpen);

  // Handle joining voice channel
  const handleJoinVoiceChannel = async (channel) => {
    // Duress: the decoy vault has no voice channels. If the UI
    // somehow exposes one, fail-closed to a toast rather than
    // betraying the shadow session with a real join RPC.
    if (isDuressMode) {
      toast.error('Voice channels unavailable');
      return;
    }
    try {
      const res = await axios.post(`${API}/voice-channels/${channel.id}/join`);
      setActiveVoiceChannel({
        ...channel,
        token: res.data.token,
        serverUrl: res.data.server_url,
      });
      toast.success(`Joined ${channel.name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join voice channel');
    }
  };

  // Handle leaving voice channel
  const handleLeaveVoiceChannel = async () => {
    if (activeVoiceChannel) {
      if (!isDuressMode) {
        try {
          await axios.post(`${API}/voice-channels/${activeVoiceChannel.id}/leave`);
        } catch (e) {
          // Network / 404 / already-left — local UI cleanup below still
          // happens, so the user isn't stuck if the server disagrees.
          if (process.env.NODE_ENV !== 'production') console.debug('leave voice failed:', e);
        }
      }
      setActiveVoiceChannel(null);
      toast.success('Left voice channel');
    }
  };

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      // Duress mode: never hit the real conversations / contacts
      // endpoints. Everything renders from the locally-decrypted
      // decoy vault so the coercer never sees a network reveal
      // their password was the duress one.
      if (isDuressMode && duressState) {
        setConversations(duressState.conversations || []);
        setContacts(duressState.contacts || []);
        return;
      }
      try {
        const [convRes, contactsRes] = await Promise.all([
          axios.get(`${API}/conversations`),
          axios.get(`${API}/contacts`),
        ]);
        setConversations(convRes.data);
        setContacts(contactsRes.data);
      } catch (error) {
        toast.error('Failed to load data');
      }
    };
    fetchData();
  }, [isDuressMode, duressState]);

  // One-shot on mount: settle any pending nudges (see
  // utils/nudgeFeedback.js). If a peer we nudged in the last 48h
  // has since rotated, the user gets a single celebratory toast
  // and the "nudges that worked" counter in Settings goes up by 1.
  // Wrapped in a ref-gated effect so React Strict Mode's double-
  // invoke doesn't double-credit.
  useEffect(() => {
    if (isDuressMode) return;
    let cancelled = false;
    (async () => {
      try {
        await checkPendingNudges();
        if (cancelled) return;
      } catch { /* silent — best-effort UX */ }
    })();
    return () => { cancelled = true; };
  }, [isDuressMode]);

  // WebSocket listener for incoming calls + push notifications
  useEffect(() => {
    const unsubscribe = addWsListener((data) => {
      if (data.type === 'call_incoming') {
        // Surface the call regardless of whether the caller is in
        // contacts — dropping the event silently when unknown was a
        // footgun (users who invited each other but never linked
        // contacts would never see incoming calls). Fetch the caller's
        // public profile on the fly; fall back to a synthetic entry so
        // the modal can still render a generic "Incoming call" prompt.
        const fromId = data.from_user_id;
        if (!fromId) return;
        const known = contacts.find((c) => c.id === fromId);
        if (known) {
          setCallData({ contact: known, isIncoming: true });
          return;
        }
        (async () => {
          try {
            const { data: profile } = await axios.get(`${API}/users/${fromId}`);
            setCallData({ contact: profile, isIncoming: true });
          } catch {
            // Last-ditch: render with just the id so the user can
            // still decline / accept based on the call itself.
            setCallData({
              contact: { id: fromId, username: 'Unknown contact', is_online: true },
              isIncoming: true,
            });
          }
        })();
      }
      // LiveKit group-call invite: the initiator sealed the 32-byte
      // room secret for us per-peer. Surface the GroupCallPanel in
      // 'incoming' phase so the user can accept/decline. We don't
      // resolve the conversation object here — the panel renders
      // fine with just the invite payload + fromId, and pulling
      // conversation metadata synchronously would block the UI on a
      // potentially-missing conversations array.
      if (data.type === 'call_signal' && data.signal_type === 'livekit_invite' && data.from_user_id) {
        const fromId = data.from_user_id;
        const convId = data.signal_data?.conversation_id;
        const conv = conversations?.find?.((c) => c.id === convId);
        // Build peers list from the conversation if we have it;
        // otherwise best-effort from just the initiator so the
        // panel can still derive a SAS and render.
        const peerIds = conv
          ? (conv.participants || []).filter((pid) => pid !== user?.id)
          : [fromId];
        const resolved = peerIds.map((pid) => {
          const known = contacts.find((c) => c.id === pid);
          return known || { id: pid, username: pid === fromId ? (known?.username || 'contact') : 'peer' };
        });
        setCallData({
          isGroup: true,
          conversation: conv || { id: convId, type: 'group', encrypted_name: 'Group call' },
          peers: resolved,
          isIncoming: true,
          invitePayload: {
            fromUserId: fromId,
            wrappedKey: data.signal_data?.wrapped_key,
          },
        });
      }
      // Privacy-first push notifications: fire only when the window is
      // hidden/blurred AND we're not in decoy mode AND permission is
      // granted. Body never includes message content — just the sender's
      // username. Muted entirely during decoy to preserve the cover.
      if (data.type === 'new_message') {
        try {
          // Sealed sender: `sender_id` is absent on new messages — we
          // fall back to deriving the sender by HMAC-matching the
          // token against known contacts, OR resolving the
          // conversation's 'other participant' for DMs so the
          // notification still shows a human-readable name.
          let senderId = data.message?.sender_id;
          if (!senderId && data.message?.conversation_id && conversations?.length) {
            const conv = conversations.find((c) => c.id === data.message.conversation_id);
            if (conv?.type === 'direct') {
              senderId = (conv.participants || []).find((p) => p !== user?.id);
            }
          }
          if (!senderId || senderId === user?.id) return;
          if (isDecoyActive) return;
          if (typeof Notification === 'undefined') return;
          if (Notification.permission !== 'granted') return;
          if (document.visibilityState === 'visible' && isWindowFocused) return;
          const sender = contacts.find((c) => c.id === senderId);
          const name = sender?.username || 'a contact';
          new Notification('Government Shield', {
            body: `New encrypted message from ${name}`,
            tag: `ghostcomm-${data.message?.conversation_id || senderId}`,
            silent: false,
          });
        } catch {}
      }
    });
    return unsubscribe;
  }, [addWsListener, contacts, user, isWindowFocused, isDecoyActive, conversations]);

  // Cmd/Ctrl+K opens the command palette. Also toggles it if already open.
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setShowCommandPalette((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Deep-link event used by "First voice verified" celebratory
  // toast inside VoiceCallModal: dispatching `gs:open-security-halo`
  // opens Settings → Privacy tab and scrolls the Security Halo
  // card into view. Decoupled via a custom event so the toast
  // doesn't need a ref/prop chain from the modal to Dashboard.
  useEffect(() => {
    const handler = () => {
      setSettingsScrollTo('security-halo');
      setShowSettings(true);
    };
    window.addEventListener('gs:open-security-halo', handler);
    return () => window.removeEventListener('gs:open-security-halo', handler);
  }, []);

  const startChat = async (contact) => {
    // Duress: conversation with a decoy contact already exists in
    // the vault. If the user opens a "start chat" UI and clicks a
    // decoy contact, just activate the existing decoy conversation
    // rather than hitting the real /conversations endpoint.
    if (isDuressMode) {
      const existing = conversations.find(
        (c) => c.type !== 'group' && (c.participants || []).includes(contact.id)
      );
      if (existing) {
        setActiveConversation(existing);
        setActiveTab('chats');
      }
      return;
    }
    try {
      const res = await axios.post(`${API}/conversations`, {
        participant_id: contact.id,
      });

      setConversations((prev) => {
        const exists = prev.find((c) => c.id === res.data.id);
        if (exists) return prev;
        return [res.data, ...prev];
      });
      setActiveConversation(res.data);
      setActiveTab('chats');
    } catch (error) {
      toast.error('Failed to start conversation');
    }
  };

  const createGroup = async (name, participantIds) => {
    if (isDuressMode) {
      toast.error('Unable to create group');
      return;
    }
    const res = await axios.post(`${API}/conversations/group`, {
      name,
      participant_ids: participantIds,
    });
    setConversations((prev) => [res.data, ...prev]);
    setActiveConversation(res.data);
    setActiveTab('chats');
    toast.success('Group created!');
  };

  const handleCall = (contact) => {
    setCallData({ contact, isIncoming: false });
  };

  /**
   * Kick off a LiveKit-backed group call for the given conversation.
   *
   * **Always re-fetches every peer's public_key from the backend
   * right before placing the call** — contacts are cached at login
   * time and can silently go stale if a peer rotates their RSA
   * keypair (e.g. logs in from a new device, which generates a
   * fresh keypair and `PUT /auth/public-key`s it). A stale cache
   * produces a successful-looking wrap on the initiator side but
   * an `OperationError` on the recipient's `unwrapSharedKey` call,
   * which the user experiences as a generic "Failed to join
   * group call". The per-peer fetch adds ~200ms on a small group
   * — acceptable cost for a voice-call setup step, and cheaper
   * than the WebSocket round-trip the initiator is about to make
   * anyway.
   */
  const handleGroupCall = useCallback(async (conversation) => {
    if (!conversation || conversation.type !== 'group') return;
    const peerIds = (conversation.participants || []).filter((pid) => pid !== user?.id);
    const fetches = peerIds.map(async (pid) => {
      try {
        const { data } = await axios.get(`${API}/users/${pid}`);
        return data;
      } catch {
        // Fall back to the cached contact row if the live fetch
        // fails (rare — only on a transient network blip). Better
        // to attempt the call with the stale key than to block
        // the user entirely; the unwrap-side error message is
        // clear enough for them to retry after a reconnect.
        return contacts.find((c) => c.id === pid) || { id: pid, username: 'peer' };
      }
    });
    const resolved = await Promise.all(fetches);
    setCallData({
      isGroup: true,
      conversation,
      peers: resolved,
      isIncoming: false,
    });
  }, [user, contacts]);

  const filteredConversations = conversations.filter((conv) => {
    if (!searchQuery) return true;
    if (conv.type === 'group') {
      return conv.encrypted_name?.toLowerCase().includes(searchQuery.toLowerCase());
    }
    const otherId = conv.participants.find((p) => p !== user.id);
    const contact = contacts.find((c) => c.id === otherId);
    return contact?.username?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const filteredContacts = contacts.filter((c) =>
    c.username.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: '#05050A' }} data-testid="dashboard">
      {/* Security Overlay when window loses focus */}
      <SecurityOverlay isVisible={!isWindowFocused} username={user?.username} />

      {/* DevTools Warning */}
      <DevToolsWarning isVisible={isDevToolsOpen} />

      <Sidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onNewChat={() => setShowNewChat(true)}
        onSettings={() => setShowSettings(true)}
        activeVoiceChannel={activeVoiceChannel}
        onJoinVoiceChannel={handleJoinVoiceChannel}
        onLeaveVoiceChannel={handleLeaveVoiceChannel}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex">
        {/* Conversation/Contact List */}
        <div className="w-80 border-r border-border flex flex-col bg-card/30 backdrop-blur-sm">
          {/* Search + Security Status */}
          <div className="p-4 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="pl-10 pr-16 bg-secondary/30 border-transparent focus:border-primary rounded-full"
                data-testid="search-input"
              />
              <button
                type="button"
                onClick={() => setShowCommandPalette(true)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-mono px-1.5 py-0.5 rounded border border-border bg-background/40 text-slate-500 hover:text-slate-300 hover:border-primary/40 transition-colors"
                aria-label="Open command palette"
                data-testid="command-palette-hint"
              >
                ⌘K
              </button>
            </div>
            {/* Security Status Badge - Click to open scanner */}
            <div className="flex items-center gap-2">
              <SecurityBadge status={securityStatus} />
              <SecurityIndicator onClick={() => setShowSecurityScanner(true)} />
            </div>
          </div>

          {activeTab === 'chats' ? (
            <ConversationList
              conversations={filteredConversations}
              activeConvId={activeConversation?.id}
              onSelect={setActiveConversation}
              contacts={contacts}
            />
          ) : (
            <ContactList
              contacts={filteredContacts}
              onStartChat={startChat}
              onContactRemoved={(contact) => {
                // Optimistic local removal — the backend has already
                // unlinked us mutually + deleted the direct conv. Drop
                // the contact and any direct conv with them so the UI
                // doesn't stutter waiting on a refetch.
                setContacts((prev) => prev.filter((c) => c.id !== contact.id));
                setConversations((prev) =>
                  prev.filter(
                    (c) =>
                      !(
                        c.type === 'direct' &&
                        Array.isArray(c.participants) &&
                        c.participants.length === 2 &&
                        c.participants.includes(contact.id)
                      ),
                  ),
                );
              }}
            />
          )}
        </div>

        {/* Chat View */}
        {activeConversation ? (
          <ChatView
            conversation={activeConversation}
            contacts={contacts}
            onCall={handleCall}
            onGroupCall={handleGroupCall}
            isWindowFocused={isWindowFocused}
            onOpenVirtualKeyboard={() => setShowVirtualKeyboard(true)}
          />
        ) : (
          <EmptyChatView />
        )}
      </div>

      {/* Dialogs */}
      <NewChatDialog
        isOpen={showNewChat}
        onClose={() => setShowNewChat(false)}
        contacts={contacts}
        onStartChat={startChat}
        onCreateGroup={createGroup}
        onOpenSettings={() => {
          setShowNewChat(false);
          setShowSettings(true);
        }}
      />

      <SettingsDialog
        isOpen={showSettings}
        onClose={() => {
          setShowSettings(false);
          setSettingsScrollTo(null);
        }}
        securityStatus={securityStatus}
        onActivateDecoy={activateDecoy}
        scrollTo={settingsScrollTo}
        contacts={contacts}
        conversations={conversations}
      />

      <SecurityScannerDialog isOpen={showSecurityScanner} onClose={() => setShowSecurityScanner(false)} />

      {!isDecoyActive && (
        <CommandPaletteOnboardingHint
          onOpenPalette={() => setShowCommandPalette(true)}
        />
      )}

      <CommandPalette
        isOpen={showCommandPalette}
        onOpenChange={setShowCommandPalette}
        conversations={conversations}
        contacts={contacts}
        currentUserId={user?.id}
        onOpenConversation={(conv) => {
          setActiveConversation(conv);
          setActiveTab('chats');
        }}
        onStartDm={startChat}
        onOpenNewChat={() => setShowNewChat(true)}
        onOpenSettings={() => setShowSettings(true)}
        onOpenLocalData={() => {
          setSettingsScrollTo('local-data');
          setShowSettings(true);
        }}
        onOpenPushSettings={() => {
          setSettingsScrollTo('push');
          setShowSettings(true);
        }}
        onOpenBlockedUsers={() => {
          setSettingsScrollTo('blocked-users');
          setShowSettings(true);
        }}
        onOpenEmergency={() => {
          setSettingsScrollTo('emergency');
          setShowSettings(true);
        }}
        onOpenSecurityScanner={() => setShowSecurityScanner(true)}
        onActivateDecoy={activateDecoy}
        onPanic={() => setShowSettings(true)}
      />

      <VirtualKeyboard
        isOpen={showVirtualKeyboard}
        onClose={() => setShowVirtualKeyboard(false)}
        onInput={(text) => {
          // Broadcast on the window so the active ChatView (which
          // owns the message compose state) can pick it up without
          // us having to lift that state. Falls back to the search
          // bar only if no chat is open AND nothing listens.
          let delivered = false;
          const handler = () => { delivered = true; };
          window.addEventListener('gs:virtual-keyboard-ack', handler, { once: true });
          window.dispatchEvent(new CustomEvent('gs:virtual-keyboard-input', { detail: text }));
          // Let listeners reply synchronously via the ack event.
          setTimeout(() => {
            window.removeEventListener('gs:virtual-keyboard-ack', handler);
            if (!delivered) {
              setSearchQuery(text);
              toast.info('Secure input → search (no chat open)');
            } else {
              toast.success('Secure input sent to chat');
            }
          }, 40);
        }}
      />

      {callData && !callData.isGroup && (
        <VoiceCallModal
          isOpen={!!callData}
          onClose={() => setCallData(null)}
          contact={callData.contact}
          isIncoming={callData.isIncoming}
        />
      )}

      {callData && callData.isGroup && (
        <GroupCallPanel
          isOpen={!!callData}
          onClose={() => setCallData(null)}
          conversation={callData.conversation}
          peers={callData.peers}
          isIncoming={callData.isIncoming}
          invitePayload={callData.invitePayload}
        />
      )}

      {/* Voice Channel Room (Discord-style) */}
      {activeVoiceChannel && (
        <VoiceChannelRoom
          channel={activeVoiceChannel}
          onLeave={handleLeaveVoiceChannel}
        />
      )}

      {/* First-run onboarding tour — self-dismisses via localStorage flag */}
      <OnboardingTour />

      {/* Multi-device key sync restore prompt — only appears on fresh devices */}
      <KeyRestorePrompt />

      {/* Always-visible panic button (top-right to avoid Emergent badge + activity banner) */}
      <FloatingPanicButton />

      {/* "Was this you?" activity banner — one-time, dismiss-to-acknowledge */}
      <SessionActivityBanner />

      {/* Silent-alert banner fired when someone logged in with the
          duress password. Self-hides in duress mode so a coerced
          unlock can never see its own alert. */}
      <DuressAlertBanner />
    </div>
  );
};

export default Dashboard;
